import {
  SITE_COPY_PARTS,
  SITE_COPY_REQUIRES,
  withCopyDependencies,
  type SiteCopyPart,
} from '@wm/domain';
import { App, Alert, Checkbox, Flex, Form, Modal, Select, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, errorMessage, useApi } from '../../api/client';
import { keys, useApiMutation } from '../../api/hooks';
import type { Company, Site, SiteCopySummary } from '../../api/types';

const ALL_PARTS: SiteCopyPart[] = [...SITE_COPY_PARTS];

/** The lists the site already has, when the API refuses to copy over them. */
function filledParts(error: unknown): SiteCopyPart[] | null {
  if (!(error instanceof ApiError) || error.problem.code !== 'site_not_empty') return null;
  return (error.problem.violations ?? []).map((v) => v.params?.part as SiteCopyPart);
}

/** "12 déchets, 4 conditionnements… et les préférences", for the message after a copy. */
export function copySummaryText(t: TFunction, s: SiteCopySummary): string {
  const counts = (['wastes', 'conditionnements', 'prestataires', 'routes', 'origins'] as const)
    .filter((part) => s[part] > 0)
    .map((part) => t(`siteCopy.counts.${part}`, { count: s[part] }));
  if (s.preferences) counts.push(t('siteCopy.counts.preferences'));
  return counts.length ? counts.join(', ') : t('siteCopy.counts.nothing');
}

/**
 * The parts to copy, each with what it brings; a filière brings its waste and prestataires, so
 * checking it checks them and unchecking them unchecks it.
 */
function CopyPartsInput({
  value = ALL_PARTS,
  onChange,
}: {
  value?: SiteCopyPart[];
  onChange?: (parts: SiteCopyPart[]) => void;
}) {
  const { t } = useTranslation();
  const change = (next: SiteCopyPart[]) => {
    const removed = value.filter((p) => !next.includes(p));
    const kept = next.filter(
      (p) => !SITE_COPY_REQUIRES[p].some((needed) => removed.includes(needed)),
    );
    onChange?.(withCopyDependencies(kept));
  };
  return (
    <Checkbox.Group
      value={value}
      onChange={(v) => change(v as SiteCopyPart[])}
      style={{ width: '100%' }}
    >
      <Flex vertical gap={8}>
        {SITE_COPY_PARTS.map((part) => (
          <Checkbox key={part} value={part}>
            <span>{t(`siteCopy.parts.${part}`)}</span>
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {t(`siteCopy.hints.${part}`)}
            </Typography.Text>
          </Checkbox>
        ))}
      </Flex>
    </Checkbox.Group>
  );
}

/** The sites of the tenant grouped by company, for the choice of the site to copy from. */
function siteOptions(sites: Site[], companies: Company[], except?: string) {
  const byCompany = new Map<
    string,
    { label: string; options: { value: string; label: string }[] }
  >();
  for (const s of sites.filter((x) => x.id !== except && x.active)) {
    const name = companies.find((c) => c.id === s.companyId)?.legalName ?? '—';
    if (!byCompany.has(s.companyId)) byCompany.set(s.companyId, { label: name, options: [] });
    byCompany.get(s.companyId)!.options.push({ value: s.id, label: s.name });
  }
  return [...byCompany.values()];
}

/**
 * The "starting configuration" of the new-site form (FR-PAR-47): an empty site by default, or the
 * configuration of another site of the tenant, CAP excluded.
 */
export function SiteCopySection({ sites, companies }: { sites: Site[]; companies: Company[] }) {
  const { t } = useTranslation();
  const source = Form.useWatch(['copyFrom', 'sourceSiteId']);
  if (!sites.length) return null;
  return (
    <>
      <div className="wm-section-title">{t('siteCopy.section')}</div>
      <Form.Item name={['copyFrom', 'sourceSiteId']} label={t('siteCopy.source')}>
        <Select
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          placeholder={t('siteCopy.emptySite')}
          options={siteOptions(sites, companies)}
        />
      </Form.Item>
      {source && (
        <>
          <Form.Item
            name={['copyFrom', 'parts']}
            initialValue={ALL_PARTS}
            rules={[{ type: 'array', min: 1, message: t('siteCopy.partsRequired') }]}
          >
            <CopyPartsInput />
          </Form.Item>
          <Alert type="info" showIcon title={t('siteCopy.notCopied')} />
        </>
      )}
    </>
  );
}

/** Copies the configuration of another site onto a site created empty (FR-PAR-47). */
export function SiteCopyModal({
  target,
  sites,
  companies,
  onClose,
}: {
  target: Site;
  sites: Site[];
  companies: Company[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ sourceSiteId: string; parts: SiteCopyPart[] }>();
  const [sourceName, setSourceName] = useState<string>();
  const copy = useApiMutation(
    (v: { sourceSiteId: string; parts: SiteCopyPart[] }) =>
      api<SiteCopySummary>(`/sites/${target.id}/actions/copy-configuration`, {
        method: 'POST',
        body: JSON.stringify(v),
      }),
    {
      invalidate: [keys.sites, keys.me, keys.wastes(target.id), keys.routes(target.id)],
      silentError: true,
      success: false,
    },
  );
  return (
    <Modal
      open
      title={t('siteCopy.title', { site: target.name })}
      okText={t('siteCopy.copy')}
      cancelText={t('common.cancel')}
      confirmLoading={copy.isPending}
      onCancel={onClose}
      onOk={() =>
        void form
          .validateFields()
          .then((v) => copy.mutateAsync(v))
          .then((summary) => {
            message.success(
              t('siteCopy.done', { source: sourceName, details: copySummaryText(t, summary) }),
            );
            onClose();
          })
          .catch((e) => {
            if (e?.errorFields) return;
            const filled = filledParts(e);
            message.error(
              filled
                ? t('siteCopy.notEmpty', {
                    parts: filled
                      .map((p) => t(`siteCopy.parts.${p}`).toLocaleLowerCase())
                      .join(', '),
                  })
                : errorMessage(e),
            );
          })
      }
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t('siteCopy.modalHint')}</Typography.Paragraph>
      <Form form={form} layout="vertical" initialValues={{ parts: ALL_PARTS }}>
        <Form.Item
          name="sourceSiteId"
          label={t('siteCopy.source')}
          rules={[{ required: true, message: t('siteCopy.sourceRequired') }]}
        >
          <Select
            showSearch={{ optionFilterProp: 'label' }}
            options={siteOptions(sites, companies, target.id)}
            onChange={(id: string) => setSourceName(sites.find((s) => s.id === id)?.name)}
          />
        </Form.Item>
        <Form.Item
          name="parts"
          rules={[{ type: 'array', min: 1, message: t('siteCopy.partsRequired') }]}
        >
          <CopyPartsInput />
        </Form.Item>
      </Form>
      <Alert type="info" showIcon title={t('siteCopy.notCopied')} />
    </Modal>
  );
}
